// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { optionalBankIbanSchema } from '@client/banks/types/account';
import dayjs from 'dayjs';
import { z } from 'zod';

/** Investment product families aligned with brokerage `asset_type`. */
export enum BankPositionAssetType {
  Stock = 'stock',
  Etf = 'etf',
  MutualFund = 'mutual_fund',
  Other = 'other',
}

export const bankPositionAssetTypeSchema = z.enum(BankPositionAssetType);

/**
 * Investment position with a single valuation point.
 *
 * @remarks
 * UI form row (camelCase). Maps 1:1 to `AdvisoryAccountsCitecPosition` via
 * `buildBulkUploadAccountData`; wire `investment` is computed as `quantity ×
 * price`.
 * @example
 *
 * ```ts
 * const position = bankPositionSchema.parse({
 *   iban: 'ES9121000418450200051332',
 *   isin: 'IE00B4L5Y983',
 *   name: 'World ETF',
 *   currency: 'EUR',
 *   assetType: BankPositionAssetType.Etf,
 *   quantity: 10,
 *   price: 85.5,
 *   snapshotDate: '2024-06-01',
 * });
 * ```
 */
export const bankPositionSchema = z.object({
  iban: optionalBankIbanSchema,
  isin: z.string().min(1),
  name: z.string().trim().min(1),
  currency: z.string(),
  assetType: bankPositionAssetTypeSchema,
  acquisitionCost: z.number().optional(),
  quantity: z.number(),
  price: z.number(),
  snapshotDate: z.string().check(({ value, issues }) => {
    const parsed = dayjs(value);

    if (parsed.isValid() && parsed.isAfter(dayjs(), 'day')) {
      issues.push({
        code: 'custom',
        input: value,
        message: 'Date cannot be in the future',
      });
    }
  }),
});

export type BankPosition = z.infer<typeof bankPositionSchema>;

/** Position row targeted by the manual bank update drawer. */
export const updateBankPositionSchema = bankPositionSchema.extend({
  id: z.number(),
});

export type UpdateBankPosition = z.infer<typeof updateBankPositionSchema>;
